import BadRequestApiError from './BadRequestApiError';
import { communicationStorage } from '../storages';
import type { ValidationProblemDetails } from './types';

const API_BASE_URL = '/api';

class ApiClient {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options?.headers as Record<string, string>,
    };

    const userAccessToken = communicationStorage.getUserAccessToken();
    if (userAccessToken)
      headers['Authorization'] = `Bearer ${userAccessToken}`;

    const cultureName = communicationStorage.getCultureName();
    if (cultureName)
      headers['Accept-Language'] = cultureName;

    const response = await fetch(url, { headers, ...options });

    if (response.status === 200)
      return await response.json();

    if (response.status === 204)
      return undefined as T;

    if (response.status === 400)
      throw new BadRequestApiError(await response.json() as ValidationProblemDetails);

    if (response.status === 404)
      return null as T;

    throw new Error(`Server error: ${response.status}`);
  }

  private async requestWithRetry<T>(endpoint: string, options?: RequestInit, retries = 3): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await this.request<T>(endpoint, options);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError')
          throw error;

        if (error instanceof BadRequestApiError)
          throw error;

        lastError = error;
        if (attempt < retries) {
          const delay = Math.max(3_000 * Math.pow(2, attempt), 30_000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.requestWithRetry<T>(endpoint, options);
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

export default new ApiClient();
